// Pure builder for the slot confirmation summary lines shown in RepeatSetupScreen.
// Kept separate from the component so it's unit-testable without the dialog provider.
import { formatDuration } from "@/utils/formatDuration";
import { getSlotStartDate } from "@/utils/getSlotStartDate";

// UTC-anchored weekday/date formatting, in lockstep with getSlotStartDate: a slot's `selectedDate`
// and computed `startDate` are UTC-midnight calendar days (see @/lib/calendarDay), so the summary
// text must read them via UTC getters/Intl `timeZone: "UTC"` rather than date-fns' local `format` —
// otherwise the schedule/start lines can name the wrong weekday or day-of-month for a viewer whose
// browser TZ isn't Europe/London (the travelling-member bug).
const utcWeekdayName = (d: Date): string => new Intl.DateTimeFormat("en-GB", { weekday: "long", timeZone: "UTC" }).format(d);
const utcMonthName = (d: Date): string => new Intl.DateTimeFormat("en-GB", { month: "long", timeZone: "UTC" }).format(d);
const utcLongDate = (d: Date): string => `${utcWeekdayName(d)} ${d.getUTCDate()} ${utcMonthName(d)} ${d.getUTCFullYear()}`;

export interface SlotSummary {
  /** e.g. "on Thursday at 09:00 for 3 hours" */
  scheduleLine: string;
  /** e.g. "Starting Thursday 6 August 2026" */
  startLine: string;
  /** R24 notice-period start date, for the occurrence list pill. */
  startDate: Date;
  /**
   * Explains the R24 one-month notice when the start is the COMPUTED default (i.e. no override). Set
   * to undefined when the user has pulled the start forward via "Start Slot Here", since then the
   * start already reflects their explicit choice and the note would be misleading.
   */
  noticeLine?: string;
}

export function buildSlotSummary(
  selectedDate: Date,
  time: string,
  durationMinutes: number,
  startOverride?: Date | null,
): SlotSummary {
  // When the user pulls the start forward via "Start Slot Here", the summary must reflect their
  // CHOSEN start, not the R24 computed default — otherwise "Starting <date>" contradicts the
  // "Your slot starts here" pill in the occurrence list.
  const startDate = startOverride ?? getSlotStartDate(selectedDate, selectedDate.getUTCDay());
  const weekday = utcWeekdayName(selectedDate);
  const hhmm = `${time.slice(0, 2)}:${time.slice(2)}`;
  return {
    scheduleLine: `on ${weekday} at ${hhmm} for ${formatDuration(durationMinutes)}`,
    startLine: `Starting ${utcLongDate(startDate)}`,
    startDate,
    // Only when we computed the default start (R24) — clarifies why it can be up to a month after
    // the picked date, and points to the pull-forward control. Suppressed once the user overrides.
    noticeLine: startOverride
      ? undefined
      : "Slots start at least one calendar month after the date you pick, so this is the earliest available start. Choose an earlier occurrence with “Start Slot Here” to begin sooner where allowed.",
  };
}
